import { useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { ROLE_LABEL } from "../../../auth/roleView";
import { useDeleteUser, useUsers } from "../../../api/queries";
import type { User } from "../../../api/types";
import { EmptyState } from "../../../components/AsyncStates";
import { Button } from "../../../components/Button";
import { DataTable, type DataTableColumn } from "../../../components/DataTable";
import { PageHeader } from "../../../components/PageHeader";
import { PaginatorControls, usePaginator } from "../../../components/Paginator";
import { QueryStates } from "../../../components/QueryStates";
import { StatePill } from "../../../components/StatePill";
import { useToast } from "../../../toast/ToastContext";
import { AdminGate } from "../AdminGate";
import { CreateUserModal } from "./CreateUserModal";
import { EditUserModal } from "./EditUserModal";
import { RemoveUserDialog } from "./RemoveUserDialog";
import { ResetPasswordModal } from "./ResetPasswordModal";
import { resolveAuthMethod, resolveStatus } from "./userAuthView";
import "./users.css";

/** `UserStatus` → the pill it renders as. `"invited"` (ADR-0037 §4's
 * pre-link state) is deliberately distinct from both Active and Inactive —
 * neither alarming (it's the expected state right after an invite) nor
 * conflatable with a deactivated account. */
function UserStatusPill({ user }: { user: User }) {
  const status = resolveStatus(user);
  if (status === "active") return <StatePill label="Active" tone="positive" shape="dot" />;
  if (status === "invited") return <StatePill label="Invited" tone="info" shape="bars" />;
  return <StatePill label="Inactive" tone="neutral" shape="ring" />;
}

function UsersTable() {
  const { session } = useAuth();
  const { pushToast } = useToast();
  // Gated by AdminGate below (only admins render this far), so the query
  // always runs enabled here.
  const paginator = usePaginator();
  const { data, isPending, isError, error, refetch } = useUsers({ cursor: paginator.cursor });
  const deleteMutation = useDeleteUser();

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<User | null>(null);
  const [resetTarget, setResetTarget] = useState<User | null>(null);
  const [removeTarget, setRemoveTarget] = useState<User | null>(null);

  function handleRemoveConfirm() {
    if (!removeTarget) return;
    const { id, username } = removeTarget;
    deleteMutation.mutate(
      { id },
      {
        onSuccess: () => {
          pushToast({ title: `Removed "${username}".`, tone: "success" });
          setRemoveTarget(null);
        },
        onError: (err) => {
          // Server guard messages ("cannot remove the last admin", "cannot
          // deactivate yourself") land here verbatim — show them as-is.
          pushToast({
            title: `Couldn't remove "${username}"`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
  }

  const newUserButton = (
    <Button variant="primary" onClick={() => setCreateOpen(true)}>
      New user
    </Button>
  );

  // Defined inside the component (rather than module-level) because cells
  // close over `session` (for the "(you)" suffix and self-removal guard) and
  // the row action setters.
  const columns: DataTableColumn<User>[] = [
    {
      key: "username",
      header: "Username",
      cell: (user) => (
        <>
          {user.username}
          {session?.userId === user.id && <span className="users-table__you"> (you)</span>}
        </>
      ),
      className: "data-table__primary",
    },
    { key: "displayName", header: "Display name", cell: (user) => user.displayName },
    { key: "role", header: "Role", cell: (user) => ROLE_LABEL[user.role] },
    {
      key: "signIn",
      header: "Sign-in",
      cell: (user) => (resolveAuthMethod(user) === "sso" ? "SSO" : "Password"),
      className: "data-table__secondary",
    },
    {
      key: "status",
      header: "Status",
      cell: (user) => <UserStatusPill user={user} />,
    },
    {
      key: "lastLogin",
      header: "Last login",
      cell: (user) => (user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : "never"),
      className: "data-table__secondary",
    },
    {
      key: "actions",
      header: <span className="visually-hidden">Actions</span>,
      cell: (user) => {
        const isSelf = session?.userId === user.id;
        // An SSO account has no password to reset (ADR-0037 §4 — it
        // authenticates only through the tenant's IdP), so the action is
        // hidden entirely rather than shown disabled with an explanation:
        // there is nothing this button could ever do for this row.
        const isSSO = resolveAuthMethod(user) === "sso";
        return (
          <div className="users-table__actions">
            <Button variant="secondary" onClick={() => setEditTarget(user)}>
              Edit
            </Button>
            {!isSSO && (
              <Button variant="secondary" onClick={() => setResetTarget(user)}>
                Reset password
              </Button>
            )}
            <Button
              variant="danger"
              onClick={() => setRemoveTarget(user)}
              disabled={isSelf}
              title={isSelf ? "You can't remove your own account." : undefined}
            >
              Remove
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader
        title="Users"
        description="Everyone who can sign in to this control plane, and what they're allowed to do."
        actions={newUserButton}
      />

      <QueryStates
        isLoading={isPending}
        loadingLabel="Loading users…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={error instanceof Error ? error.message : "Couldn't load users. Check your connection and try again."}
        isEmpty={Boolean(data && data.items.length === 0)}
        empty={
          <EmptyState
            title="No users yet"
            description="Create the first account to let someone else sign in."
            action={newUserButton}
          />
        }
      >
        {data && (
          <>
            <DataTable columns={columns} rows={data.items} rowKey={(user) => user.id} />
            <PaginatorControls itemCount={data.items.length} nextCursor={data.nextCursor} paginator={paginator} noun="users" />
          </>
        )}
      </QueryStates>

      <CreateUserModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <EditUserModal user={editTarget} onClose={() => setEditTarget(null)} />
      <ResetPasswordModal user={resetTarget} onClose={() => setResetTarget(null)} />
      <RemoveUserDialog
        user={removeTarget}
        isRemoving={deleteMutation.isPending}
        onCancel={() => setRemoveTarget(null)}
        onConfirm={handleRemoveConfirm}
      />
    </>
  );
}

export function UsersPage() {
  return (
    <AdminGate>
      <UsersTable />
    </AdminGate>
  );
}
